import { useState, useEffect } from "react";
import { Check, Star, Crown, Gift, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Banner from "@/components/Banner";
import { useNavigate } from "react-router-dom";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";
import { useCurrency } from "@/hooks/useCurrency";
import { DynamicPrice } from "@/components/ui/DynamicPrice";
import { useAuth } from "@/hooks/useAuth";
import { useAuthCheck } from "@/hooks/useAuthCheck";
import AuthModal from "@/components/auth/AuthModal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BreadcrumbNavigation } from "@/components/BreadcrumbNavigation";

// Declare Razorpay types for TypeScript
declare global {
  interface Window {
    Razorpay: any;
  }
}

const Pricing = () => {
  const navigate = useNavigate();
  const { hasAccess, refreshStatus } = usePremiumAccess();
  const { user } = useAuth();
  const { user: fullUser, isProfileComplete } = useAuthCheck();
  
  const { currency, loading: currencyLoading } = useCurrency();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shouldTriggerPayment, setShouldTriggerPayment] = useState(false);
  const { toast } = useToast();

  const freeFeatures = [
    "Featured Story of the Day",
    "Basic and Advanced Math Activities like Numbers,Addition, Subtraction, Multiplication ",
    "Alphabet Learning (A-Z)", 
    "Science Lessons and Geography Introduction",
    "Community Helpers,About World and History Knowledge",
    "Interactive section of Shapes, Measurement,Time and Money",
    "Free YouTube Video Gallery",
    "Easy English Grammer Learnings",
    "Brain Teasers & Logic Games and Sentence Building Activities",
    "Interactive and Learning Games",
  ];

  const premiumFeatures = [
    
    "ALL Free Features",
    "Complete Stories Collection",
    "Interactive Audio Stories",
    "Downloadable Printables & Worksheets",
    "All Upcoming Stories Access",
    "All Upcoming Printables & Worksheets download Access",
    "All Upcoming Premium Learning Modules Access",
    "Ad-Free Experience",
    "Contact Support",
   
  ];

  // Auto-trigger payment after successful login (not signup)
  useEffect(() => {
    if (shouldTriggerPayment && user && !hasAccess) {
      setShouldTriggerPayment(false);
      // Small delay to ensure auth state is fully settled
      setTimeout(() => {
        initiatePayment();
      }, 500);
    }
  }, [user, shouldTriggerPayment, hasAccess]);

  // Listen for auth state changes to handle post-signup flow
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user && shouldTriggerPayment) {
        // User has successfully logged in after signup verification
        setShouldTriggerPayment(false);
        setTimeout(() => {
          initiatePayment();
        }, 500);
      }
    });

    return () => subscription.unsubscribe();
  }, [shouldTriggerPayment]);

  const initiatePayment = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    // Check if currency is enabled
    if (!currency.enabled) {
      toast({
        title: "Payment Currently Unavailable",
        description: currency.code === 'USD' 
          ? "USD payments will be available once Razorpay International is approved (approximately 2 months). Indian users can pay in INR."
          : "This payment method is currently disabled.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Initiating payment process for currency:', currency.code);
      
      // Create payment order - this will work for both INR and USD
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          currency: currency.code,
          amount: currency.price
        }
      });

      if (error) {
        console.error('Error creating payment:', error);
        toast({
          title: "Payment Error",
          description: "Failed to initiate payment. Please try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('Payment order created:', data);

      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        
        await new Promise((resolve) => {
          script.onload = resolve;
        });
      }

      // Configure Razorpay options
      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "TinyMinds Playtime Hub",
        description: "Premium Access - 1 Year Subscription",
        order_id: data.orderId,
        handler: async (response: any) => {
          console.log('Payment successful:', response);
          
          try {
            // Verify payment
            const verifyResult = await supabase.functions.invoke('verify-razorpay-payment', {
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            });

            if (verifyResult.error) {
              console.error('Payment verification failed:', verifyResult.error);
              toast({
                title: "Verification Failed",
                description: "Payment verification failed. Please contact support.",
                variant: "destructive",
              });
              return;
            }

            console.log('Payment verified successfully');
            
            // Refresh premium status
            await refreshStatus();
            
            toast({
              title: "🎉 You're now Premium!",
              description: "Welcome to Premium! You now have access to all premium features.",
            });

            // Redirect to homepage
            navigate('/');
            
          } catch (verifyError) {
            console.error('Error verifying payment:', verifyError);
            toast({
              title: "Verification Error",
              description: "There was an issue verifying your payment. Please contact support.",
              variant: "destructive",
            });
          }
        },
        prefill: {
          email: user.email,
          name: data.userProfile?.full_name || user.user_metadata?.full_name || "",
          contact: data.userProfile?.phone_number || "",
        },
        theme: {
          color: "#FF6B6B"
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed');
            setIsProcessing(false);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Error initiating payment:', error);
      toast({
        title: "Payment Error",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgrade = () => {
    if (!user) {
      // User not logged in - show auth modal and set flag to trigger payment after login
      setShouldTriggerPayment(true);
      setIsAuthModalOpen(true);
    } else {
      // User is logged in - go straight to payment
      initiatePayment();
    }
  };

  const handleAuthModalClose = () => {
    setIsAuthModalOpen(false);
    // Reset payment trigger flag if modal is closed without authentication
    if (!user) {
      setShouldTriggerPayment(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
        {/* Floating shapes */}
        <div className="absolute top-32 left-16 w-16 h-16 bg-purple-200 rounded-full opacity-25 animate-bounce" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
        <div className="absolute top-56 right-24 w-20 h-20 bg-pink-200 rounded-full opacity-30 animate-pulse" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
        <div className="absolute bottom-48 left-32 w-14 h-14 bg-yellow-200 rounded-full opacity-35 animate-bounce" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
        <div className="absolute top-72 left-1/2 w-18 h-18 bg-blue-200 rounded-full opacity-30 animate-pulse" style={{ animationDelay: '0.5s', animationDuration: '4.5s' }}></div>
        <div className="absolute bottom-32 right-16 w-12 h-12 bg-green-200 rounded-full opacity-40 animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '3s' }}></div>
        
        <div className="absolute top-96 left-1/4 text-2xl opacity-30 animate-bounce" style={{ animationDelay: '3s', animationDuration: '6s' }}>💰</div>
        <div className="absolute bottom-72 right-1/4 text-xl opacity-35 animate-pulse" style={{ animationDelay: '4s', animationDuration: '5s' }}>👑</div>
        <div className="absolute top-64 right-12 text-lg opacity-25 animate-bounce" style={{ animationDelay: '1s', animationDuration: '4.5s' }}>⭐</div>
        <div className="absolute bottom-96 left-12 text-xl opacity-30 animate-pulse" style={{ animationDelay: '5s', animationDuration: '3.5s' }}>🎯</div>
      </div>

      <Banner />
      <Navigation />
      <BreadcrumbNavigation 
        items={[
          { label: 'Home', href: '/' },
          { label: 'Pricing' }
        ]}
        className="container mx-auto px-4 mt-6"
      />
      
      <main className="container mx-auto px-4 py-8 relative z-10">
        {/* Page Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-6xl font-fredoka font-bold text-tinyminds-blue mb-4">
            💰 Choose Your Plan 💰
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
           Explore fun and interactive learning with our complete educational platform for kids!
          </p>

          <p className="text-xl font-fredoka text-tinyminds-coral mb-2">
           Premium Available for Users Worldwide
          </p>

          <p className="text-xl font-fredoka text-tinyminds-red mb-2">
          Check out our <a href="/terms-of-service" className="underline text-blue-600 hover:text-blue-800">Terms of Service</a> page before payment.
          </p>
          
          <div className="mt-4">
            <DynamicPrice 
              className="inline-flex flex-col items-center"
              showNote={true}
              noteClassName="font-medium text-tinyminds-purple"
            />
          </div>
        </div>

        {/* Current Status */}
        {hasAccess && (
          <div className="text-center mb-8">
            <Badge className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 text-lg font-fredoka shadow-lg">
              <Crown className="w-5 h-5 mr-2" />
              You're Currently on Premium! 🎉
            </Badge>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Free Plan */}
          <Card className="relative overflow-hidden shadow-xl border-3 border-tinyminds-lightPurple hover:shadow-2xl transition-all duration-300 animate-scale-in">
            <div className="absolute top-4 right-4">
              <Badge className="bg-tinyminds-skyblue text-white">
                <Gift className="w-4 h-4 mr-1" />
                Free
              </Badge>
            </div>
            
            <CardHeader className="text-center p-8 bg-gradient-to-br from-tinyminds-lightPurple/30 to-white">
              <div className="text-6xl mb-4">🎁</div>
              <CardTitle className="text-3xl font-fredoka text-tinyminds-blue mb-2">
                Free Plan
              </CardTitle>

              <div className="text-4xl font-bold text-tinyminds-coral mb-2">
                {currencyLoading ? (
                  <div className="animate-pulse bg-gray-200 h-8 w-24 mx-auto rounded"></div>
                ) : (
                  <>
                    <span className="inline-flex items-center">
                      <span className="mr-1">{currency.symbol}</span>0
                    </span>
                  </>
                )}
              </div>

              <p className="text-gray-600">
                Perfect for getting started with basic learning activities
              </p>
            </CardHeader>
            
            <CardContent className="p-8">
              <div className="space-y-4 mb-8">
                {freeFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                className="w-full bg-tinyminds-skyblue hover:bg-tinyminds-skyblue/90 text-white font-fredoka py-3 text-lg"
                disabled
              >
                Current Plan
              </Button>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <div className="relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-2 shadow-lg">
                <Star className="w-4 h-4 mr-1" />
                Best Value
              </Badge>
            </div>
            
            <Card className="relative overflow-hidden shadow-xl border-4 border-gradient-to-r from-tinyminds-coral to-tinyminds-yellow hover:shadow-2xl transition-all duration-300 animate-scale-in transform hover:scale-105 mt-4">
              <div className="absolute top-4 right-4">
                <Badge className="bg-gradient-to-r from-tinyminds-coral to-tinyminds-yellow text-white animate-pulse">
                  <Crown className="w-4 h-4 mr-1" />
                  Most Popular
                </Badge>
              </div>
            
            <CardHeader className="text-center p-8 bg-gradient-to-br from-tinyminds-coral/20 via-tinyminds-yellow/20 to-white">
              <div className="text-6xl mb-4">👑</div>
              <CardTitle className="text-3xl font-fredoka text-tinyminds-blue mb-2">
                Premium Plan
              </CardTitle>
              <div className="text-4xl font-bold text-tinyminds-coral mb-2">
                {currencyLoading ? (
                  <div className="animate-pulse bg-gray-200 h-8 w-24 mx-auto rounded"></div>
                ) : (
                  <>
                    <DynamicPrice showNote={false} />
                    <span className="text-lg text-gray-600 font-normal">/year</span>
                  </>
                )}
              </div>
              <p className="text-gray-600">
               Explore all content and unlock every premium feature without limits
              </p>
            </CardHeader>
            
            <CardContent className="p-8">
              <div className="space-y-4 mb-8 max-h-96 overflow-y-auto">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    {freeFeatures.includes(feature) ? (
                      <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Star className="w-5 h-5 text-tinyminds-coral mt-0.5 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${freeFeatures.includes(feature) ? 'text-gray-600' : 'text-tinyminds-blue font-medium'}`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              
              <Button 
                  onClick={handleUpgrade}
                  className="w-full bg-gradient-to-r from-tinyminds-coral to-tinyminds-yellow hover:from-tinyminds-yellow hover:to-tinyminds-coral text-orange-900 font-bold font-fredoka py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  disabled={hasAccess || isProcessing || !currency.enabled}
                >
                {hasAccess ? (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Already Premium
                  </>
                ) : isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : !currency.enabled ? (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Coming Soon ({currency.code})
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Upgrade to Premium
                  </>
                )}
              </Button>
            </CardContent>
            </Card>
          </div>
        </div>

        {/* Features Comparison */}
        <div className="mt-16 text-center">
          <h2 className="text-3xl font-fredoka font-bold text-tinyminds-purple mb-8">
            Why Choose Premium? 🌟
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border-2 border-tinyminds-lightPurple">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-xl font-fredoka text-tinyminds-purple mb-2">Complete Content</h3>
              <p className="text-gray-600 text-sm">Access to all stories, activities, and educational materials</p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border-2 border-tinyminds-lightPurple">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-fredoka text-tinyminds-purple mb-2">Advanced Learning</h3>
              <p className="text-gray-600 text-sm">All Upcoming features and Printable material for unlimited downloads</p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg border-2 border-tinyminds-lightPurple">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-fredoka text-tinyminds-purple mb-2">Premium Support</h3>
              <p className="text-gray-600 text-sm">Priority support and regular content updates</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleAuthModalClose}
      />
    </div>
  );
};

export default Pricing;
